/**
 * VPA — Volume Spread Analysis signal markers.
 *
 * Faithful port of the VSA signal section of karthikmarar's "VPA 5.0" Pine v4
 * study (the `plotshape(...)` block under group "VSA Signals"). Only the
 * Bullish / Bearish / Warning *signal markers* are ported — not the bar
 * colouring, EMAs/VWAP, CPR, bands, AlphaTrend, S/R or Hang zones (those are
 * separate chart features). Every supporting definition the signals depend on
 * (basic VSA reads + the RWI short/long-term trend gates `upmid`/`upmajor`/
 * `upminor`) is ported with the Pine defaults; only the per-signal show toggles
 * are user-configurable (Chart Settings → VPA).
 *
 * Computed on raw CLOSED OHLC (forming bar excluded by the caller) so markers
 * are repaint-safe and stable across the chart's display candle type; the chart
 * snaps each emitted bar-time to the nearest display bar and places the shape
 * above / below the bar (never at an absolute price) so markers stay visible on
 * Regular / Heikin-Ashi / CSL / CLH candles alike.
 *
 * Pine→here mappings: triangleup→arrowUp, triangledown→arrowDown (lightweight-
 * charts has no triangle, matching the existing FLLHH/AS signals). Two-line Pine
 * labels (e.g. "ST\nSV") are joined with "·". The original script's FOM bug
 * (plotted under `showtb`) is corrected to its own `fom` toggle.
 */
import { CandleRow } from '../components/charts/TradingChart';

/** One toggle per VSA signal group — all default false (Pine `defval=false`). */
export interface VpaToggles {
  ut: boolean;    // Up Thrust (UT1/UT2)
  trb: boolean;   // Top Reversal Bar
  tc: boolean;    // Trend Change
  put: boolean;   // Pseudo Up Thrust (PUT/PUC)
  nd: boolean;    // No Demand
  ns: boolean;    // No Supply
  lvtst: boolean; // Low Volume Supply Test (LVT/ST)
  st: boolean;    // Strength signals (ST1..ST4)
  sv: boolean;    // Stop Volume
  eu: boolean;    // Effort Up
  euf: boolean;   // Effort Up Fail
  ed: boolean;    // Effort Down
  edf: boolean;   // Effort Down Fail
  rut: boolean;   // Reverse Up Thrust
  bce: boolean;   // Buy Condition Exist
  sce: boolean;   // Sell Condition Exist
  bc: boolean;    // Buying Climax
  sc: boolean;    // Selling Climax
  tb: boolean;    // Two Bar Reversal
  fom: boolean;   // Ease of Movement
}

export const VPA_TOGGLE_DEFAULTS: VpaToggles = {
  ut: false, trb: false, tc: false, put: false, nd: false, ns: false,
  lvtst: false, st: false, sv: false, eu: false, euf: false, ed: false,
  edf: false, rut: false, bce: false, sce: false, bc: false, sc: false,
  tb: false, fom: false,
};

/** Marker colour semantics (bull = green, bear = red, warn = amber). */
export type VpaPolarity = 'bull' | 'bear' | 'warn';
/** Settings-UI grouping — exactly two columns: Bullish vs Bearish. The three
 *  warning signals are grouped by their directional lean / above-vs-below-bar
 *  placement (ND → bearish; FOM, TB → bullish) while keeping their amber
 *  marker colour. */
export type VpaGroup = 'bull' | 'bear';

/** Settings-UI metadata: key, short code, label, marker polarity and UI group. */
export interface VpaSignalDef { key: keyof VpaToggles; code: string; label: string; polarity: VpaPolarity; group: VpaGroup; tip: string; }
export const VPA_SIGNAL_DEFS: VpaSignalDef[] = [
  // ── Bullish column (strength + below-bar reversals) ──
  { key: 'ns',    code: 'NS',  label: 'No Supply',           polarity: 'bull', group: 'bull',
    tip: 'No Supply (strength). Fires when: down-bar AND narrow-range bar AND low-volume AND mid-close (close in the 30–70% band of the bar). Reading: sellers cannot push price down — supply has dried up.' },
  { key: 'lvtst', code: 'LVT', label: 'Low-Vol Supply Test', polarity: 'bull', group: 'bull',
    tip: 'Low-Volume Supply Test (strength) — two markers. LVT: this bar makes the lowest low of the last 5 yet closes in the top 30% (up-close) on low-volume, AND no Effort-Down-Fail on the bar. ST: the bar immediately AFTER an LVT is an up-bar with an up-close (confirms the test), AND no Effort-Up on the bar.' },
  { key: 'st',    code: 'ST',  label: 'Strength Signals',    polarity: 'bull', group: 'bull',
    tip: 'Strength returning after a fall (ST1..ST4). Base: an up-bar that follows a down-bar, closes in the upper 40% (ClosePos>3), volume not low (volpos<4), with trend gates down (upminor<0 AND upmid≤0). ST1 also requires upmajor<0; ST3 also requires volume ≥1.5×avg; ST2 is the very-high-close variant (prior bar volume < avg, very-high-close, volpos<4). All hidden if an Effort-Up, Effort-Down-Fail or Stop-Volume fires on the same bar.' },
  { key: 'sv',    code: 'SV',  label: 'Stop Volume',         polarity: 'bull', group: 'bull',
    tip: 'Stopping Volume (strength). Fires when: this bar makes the lowest low of the last 5 AND closes mid-or-up (up-close or mid-close) AND volume >1.5×avg AND the major trend is down (upmajor<0). Reading: heavy buying is absorbing the down-move. Marker shows "ST·SV" if a Strength signal also fired this bar.' },
  { key: 'eu',    code: 'EU',  label: 'Effort Up',           polarity: 'bull', group: 'bull',
    tip: 'Effort to move Up (bullish). Fires when: higher high AND higher low AND higher close AND close in the top 30% AND wide spread (> avg spread) AND volume not low (volpos<4). Marker "ST·EU" if strength also present; suppressed when a Buy-Condition is active on the bar.' },
  { key: 'edf',   code: 'EDF', label: 'Effort Down Fail',    polarity: 'bull', group: 'bull',
    tip: 'Effort-Down Failed (bullish). Fires on the bar AFTER an Effort-Down when price reverses up — i.e. (up-bar with above-avg spread) OR a Reverse-Up-Thrust OR a Buy-Condition on this bar. Marker "ST·EDF" if strength also present.' },
  { key: 'rut',   code: 'RUT', label: 'Reverse Up Thrust',   polarity: 'bull', group: 'bull',
    tip: 'Reverse Up Thrust (bullish). Fires when: up-bar AND up-close AND volume rising (>prev) AND volume >avg AND wide-range bar AND the previous bar was a down-bar that closed down AND the immediate trend is down (upminor<0). Hidden if a Strength signal is already firing.' },
  { key: 'bce',   code: 'BCE', label: 'Buy Condition',       polarity: 'bull', group: 'bull',
    tip: 'Buy Condition exists (strength returns). Fires when: an up-bar occurs on the bar AFTER a strength setup (previous bar was Strength ST2/ST3). Marker "EU·BCE" when it coincides with an Effort-Up; plain "BCE" only when no Strength / Effort-Up / Low-Vol-Test on the same bar.' },
  { key: 'sc',    code: 'SC',  label: 'Selling Climax',      polarity: 'bull', group: 'bull',
    tip: 'Selling Climax — end of the down-trend. Fires when: wide-range bar AND close in the bottom half (below-close) AND volume = highest of the last 60 bars AND major trend down (upmajor = −1). Marker "ST·SC" if strength also present.' },
  { key: 'tb',    code: 'TB',  label: 'Two-Bar Reversal',    polarity: 'warn', group: 'bull',
    tip: 'Two-Bar Reversal. Requires today to engulf yesterday (lower low AND higher high). UP reversal (below bar): the prior close was below each of the previous 4 closes AND today up-closes — green if volume >1.2×avg, amber if ≤. DOWN reversal (above bar): the prior close was above each of the previous 4 closes AND today down-closes — red if volume >1.2×avg, amber if ≤.' },
  { key: 'fom',   code: 'FOM', label: 'Ease of Movement',    polarity: 'warn', group: 'bull',
    tip: 'Ease of Movement / no-result bar (amber, below bar). Fires when: volume >1.5×avg BUT the body barely moves — |close−open| < half its 40-bar average. Reading: big effort, little result → possible absorption / supply-demand area.' },
  // ── Bearish column (weakness + above-bar reversals) ──
  { key: 'ut',  code: 'UT',  label: 'Up Thrust',         polarity: 'bear', group: 'bear',
    tip: 'Up Thrust (weakness) — UT1 / UT2. UT1: wide-range bar + down-close + higher high than prior, while the short-term trend is up (upmid=1); OR the "true" variant — close in the bottom 20% (ClosePos=1), major trend up, volume not very-high (volpos<4). UT2: the bar AFTER an Up-Thrust that is a down-bar (and not narrow-range, OR on volume >1.3×avg); OR a new-5-bar-high down-close upthrust on high-volume (NUT). Both hidden if Effort-Up-Fail, Sell-Condition or Buying-Climax fire on the bar.' },
  { key: 'trb', code: 'TRB', label: 'Top Reversal Bar',  polarity: 'bear', group: 'bear',
    tip: 'Top Reversal Bar (weakness). Fires when: the previous bar was an up-bar on above-avg volume and wide-range, AND today is a wide-range down-bar that down-closes AND makes the highest high of the last 10, AND major trend up. Hidden if Sell-Condition, an Up-Thrust, or Effort-Up-Fail fire.' },
  { key: 'tc',  code: 'TC',  label: 'Trend Change',      polarity: 'bear', group: 'bear',
    tip: 'Trend Change (weakness). Fires when: after an up-bar at a 5-bar high, today is a (NOT wide-range) down-bar closing down or mid, on volume >avg, with both major and short-term trends up — high-volume stalling at the top. Must NOT be a Pseudo-Up-Thrust. Hidden if Effort-Up-Fail.' },
  { key: 'put', code: 'PUT', label: 'Pseudo Up Thrust',  polarity: 'bear', group: 'bear',
    tip: 'Pseudo Up Thrust (weakness) — PUT / PUC. PUT: previous bar up, today a higher high, previous volume >1.5×avg, today a down-bar that down-closes — but NOT a genuine Up-Thrust. PUC: the down-closing down-bar AFTER a PUT (confirms). Both hidden if Effort-Up-Fail.' },
  { key: 'euf', code: 'EUF', label: 'Effort Up Fail',    polarity: 'bear', group: 'bear',
    tip: 'Effort-Up Failed (bearish). Fires on the bar AFTER an Effort-Up when it turns weak — an Up-Thrust / UT-condition (1/2/3), OR a down-bar with above-avg spread. Hidden if an Up-Thrust marker is already shown on the bar.' },
  { key: 'ed',  code: 'ED',  label: 'Effort Down',       polarity: 'bear', group: 'bear',
    tip: 'Effort to move Down (bearish). Fires when: lower high AND lower low AND lower close AND close in the bottom 25% AND wide-range bar AND volume rising (>prev). Hidden if Effort-Up-Fail on the bar.' },
  { key: 'sce', code: 'SCE', label: 'Sell Condition',    polarity: 'bear', group: 'bear',
    tip: 'Sell Condition exists (weakness returns). Fires when: a fresh Up-Thrust condition (UT1/UT2/UT3) is present this bar that was NOT present on the previous bar. Marker "UT·SCE" if an Up-Thrust marker also shows.' },
  { key: 'bc',  code: 'BC',  label: 'Buying Climax',     polarity: 'bear', group: 'bear',
    tip: 'Buying Climax — end of the up-trend. Fires when: wide-range bar AND close in the upper half (above-close) AND volume = highest of the last 60 bars AND major trend up (upmajor = +1). Marker "UT·BC" if an Up-Thrust also fires.' },
  { key: 'nd',  code: 'ND',  label: 'No Demand',         polarity: 'warn', group: 'bear',
    tip: 'No Demand (weakness, amber above bar). Fires when: up-bar AND narrow-range bar AND low-volume AND close in the upper half (above-close or up-close). Reading: no buying interest behind the rise. Fires both in an up-trend (upminor≥0 and upmid≥0) and in a down/sideways context (upminor≤0 or upmid≤0).' },
];

export interface VpaMarker {
  time: number;
  position: 'aboveBar' | 'belowBar';
  shape: 'arrowUp' | 'arrowDown' | 'circle' | 'square';
  text: string;
  color: string;
}

/**
 * R131 — resolve a marker's display text (e.g. "UT1", "ST·SV", "EU·BCE") to
 * the signal definition(s) behind it, so the crosshair tooltip can show the
 * same firing-condition description the Chart Settings toggles carry.
 * Combined markers ("ST·SV") return one entry per component.
 */
const MARKER_CODE_TO_KEY: Record<string, keyof VpaToggles> = {
  UT1: 'ut', UT2: 'ut', TRB: 'trb', TC: 'tc', PUT: 'put', PUC: 'put',
  ND: 'nd', NS: 'ns', LVT: 'lvtst',
  ST1: 'st', ST2: 'st', SV: 'sv', EU: 'eu', EUF: 'euf', ED: 'ed',
  EDF: 'edf', RUT: 'rut', BCE: 'bce', SCE: 'sce', BC: 'bc', SC: 'sc',
  TB: 'tb', FOM: 'fom',
};
export function vpaDefsForMarkerText(text: string): VpaSignalDef[] {
  const parts = String(text || '').split('·').map(p => p.trim()).filter(Boolean);
  const out: VpaSignalDef[] = [];
  for (let i = 0; i < parts.length; i++) {
    const code = parts[i].toUpperCase();
    // "ST" is ambiguous: standalone = the Low-Vol Supply-Test confirmation
    // marker (lvtst); as a combo prefix ("ST·SV") = the Strength signals (st).
    const key: keyof VpaToggles | undefined =
      code === 'ST' ? (parts.length > 1 ? 'st' : 'lvtst') : MARKER_CODE_TO_KEY[code];
    if (!key) continue;
    const def = VPA_SIGNAL_DEFS.find(d => d.key === key);
    if (def && !out.includes(def)) out.push(def);
  }
  return out;
}

const COL_BULL = '#26a69a';
const COL_BEAR = '#ef5350';
const COL_WARN = '#f7b500';

/* ── small math helpers (Pine equivalents) ─────────────────────────────── */
function sma(src: number[], p: number): number[] {
  const n = src.length, out = new Array<number>(n).fill(NaN);
  if (p <= 0) return out;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    sum += src[i];
    if (i >= p) sum -= src[i - p];
    if (i >= p - 1) out[i] = sum / p;
  }
  return out;
}
function ema(src: number[], p: number): number[] {
  const n = src.length, out = new Array<number>(n).fill(NaN);
  if (!n || p <= 0) return out;
  const k = 2 / (p + 1);
  let prev = NaN;
  for (let i = 0; i < n; i++) {
    const v = src[i];
    if (!Number.isFinite(v)) { out[i] = prev; continue; }
    prev = Number.isFinite(prev) ? (v - prev) * k + prev : v;
    out[i] = prev;
  }
  return out;
}
/** Wilder's RMA (used by Pine's atr). */
function rma(src: number[], p: number): number[] {
  const n = src.length, out = new Array<number>(n).fill(NaN);
  if (p <= 0) return out;
  let prev = NaN, seed = 0, cnt = 0;
  for (let i = 0; i < n; i++) {
    const v = src[i];
    if (!Number.isFinite(v)) { out[i] = prev; continue; }
    if (!Number.isFinite(prev)) {
      seed += v; cnt++;
      if (cnt >= p) { prev = seed / p; out[i] = prev; }
    } else {
      prev = (prev * (p - 1) + v) / p;
      out[i] = prev;
    }
  }
  return out;
}
function atr(H: number[], L: number[], C: number[], p: number): number[] {
  const n = H.length, tr = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    if (i === 0) tr[i] = H[i] - L[i];
    else tr[i] = Math.max(H[i] - L[i], Math.abs(H[i] - C[i - 1]), Math.abs(L[i] - C[i - 1]));
  }
  return rma(tr, p);
}
/** highest(src, n) over the window ending at i (incl. current); NaN if < n bars. */
function highestAt(src: number[], i: number, n: number): number {
  if (i < n - 1) return NaN;
  let m = -Infinity;
  for (let k = i - n + 1; k <= i; k++) if (src[k] > m) m = src[k];
  return m;
}
function lowestAt(src: number[], i: number, n: number): number {
  if (i < n - 1) return NaN;
  let m = Infinity;
  for (let k = i - n + 1; k <= i; k++) if (src[k] < m) m = src[k];
  return m;
}
/** Pine nz(src[shift]) with default 0. */
function nzPrev(src: number[], i: number, shift: number): number {
  const j = i - shift;
  return j >= 0 && Number.isFinite(src[j]) ? src[j] : 0;
}

/** Minimum closed bars before VPA can produce valid signals (highest(vol,60) +
 *  long RWI plmax=40 + sma(40) all need warm-up). */
export const VPA_MIN_BARS = 65;

/**
 * Compute VPA signal markers for the enabled toggles over CLOSED candles.
 * Mirrors the Pine plotshape conditions verbatim (including the `not …` guards).
 */
export function computeVpaMarkers(closed: CandleRow[], toggles: VpaToggles): VpaMarker[] {
  const n = closed.length;
  if (n < VPA_MIN_BARS) return [];

  const O = closed.map(c => c.open);
  const H = closed.map(c => c.high);
  const L = closed.map(c => c.low);
  const C = closed.map(c => c.close);
  const V = closed.map(c => Number(c.volume) || 0);
  const T = closed.map(c => c.time);

  const spread = H.map((h, i) => h - L[i]);
  const volAvg = sma(V, 40);
  const avgSpread = sma(spread, 40);
  const pm = C.map((c, i) => Math.abs(c - O[i]));
  const pma = ema(pm, 40);

  // RWI short (psmin=2, psmax=8) → upmid; long (plmin=10, plmax=40) → j/j2 → upmajor/upminor.
  const atr2 = atr(H, L, C, 2), atr8 = atr(H, L, C, 8);
  const atr10 = atr(H, L, C, 10), atr40 = atr(H, L, C, 40);
  const SQ2 = Math.sqrt(2), SQ8 = Math.sqrt(8), SQ10 = Math.sqrt(10), SQ40 = Math.sqrt(40);

  const upmid = new Array<number>(n).fill(0);
  const upmajor = new Array<number>(n).fill(0);
  const upminor = new Array<number>(n).fill(-1);
  for (let i = 0; i < n; i++) {
    const rshmin = atr2[i] > 0 ? (H[i] - nzPrev(L, i, 2)) / (atr2[i] * SQ2) : NaN;
    const rshmax = atr8[i] > 0 ? (H[i] - nzPrev(L, i, 8)) / (atr8[i] * SQ8) : NaN;
    const RWIHi = Math.max(Number.isFinite(rshmin) ? rshmin : -Infinity, Number.isFinite(rshmax) ? rshmax : -Infinity);
    upmid[i] = Number.isFinite(RWIHi) && RWIHi > 1 ? 1 : 0;

    const rlhmin = atr10[i] > 0 ? (H[i] - nzPrev(L, i, 10)) / (atr10[i] * SQ10) : NaN;
    const rlhmax = atr40[i] > 0 ? (H[i] - nzPrev(L, i, 40)) / (atr40[i] * SQ40) : NaN;
    const RWILHi = Math.max(Number.isFinite(rlhmin) ? rlhmin : -Infinity, Number.isFinite(rlhmax) ? rlhmax : -Infinity);
    const rllmin = atr10[i] > 0 ? (nzPrev(H, i, 10) - L[i]) / (atr10[i] * SQ10) : NaN;
    const rllmax = atr40[i] > 0 ? (nzPrev(H, i, 40) - L[i]) / (atr40[i] * SQ40) : NaN;
    const RWILLo = Math.max(Number.isFinite(rllmin) ? rllmin : -Infinity, Number.isFinite(rllmax) ? rllmax : -Infinity);
    const j2 = RWILHi, k2 = RWILLo;
    const j = (Number.isFinite(j2) ? j2 : 0) - (Number.isFinite(k2) ? k2 : 0);
    upmajor[i] = j > 1 ? 1 : j < -1 ? -1 : 0;
    upminor[i] = Number.isFinite(j2) && j2 > 1 ? 1 : -1;
  }

  // Per-bar VSA boolean arrays (filled forward so compound[i-1] reads are valid).
  const upThrustBar = new Array<boolean>(n).fill(false);
  const upThrustBartrue = new Array<boolean>(n).fill(false);
  const upThrustCond1 = new Array<boolean>(n).fill(false);
  const upThrustCond2 = new Array<boolean>(n).fill(false);
  const upThrustCond3 = new Array<boolean>(n).fill(false);
  const nut = new Array<boolean>(n).fill(false);
  const pseudoUp = new Array<boolean>(n).fill(false);
  const pseudoUtCond = new Array<boolean>(n).fill(false);
  const topRevBar = new Array<boolean>(n).fill(false);
  const trendChange = new Array<boolean>(n).fill(false);
  const noDemandUt = new Array<boolean>(n).fill(false);
  const noDemandDt = new Array<boolean>(n).fill(false);
  const noSupply = new Array<boolean>(n).fill(false);
  const lowVolTest = new Array<boolean>(n).fill(false);
  const lowVolTest2 = new Array<boolean>(n).fill(false);
  const sellCond1 = new Array<boolean>(n).fill(false);
  const sellCond = new Array<boolean>(n).fill(false);
  const strDown0 = new Array<boolean>(n).fill(false);
  const strDown = new Array<boolean>(n).fill(false);
  const strDown1 = new Array<boolean>(n).fill(false);
  const strDown2 = new Array<boolean>(n).fill(false);
  const strcond = new Array<boolean>(n).fill(false);
  const buyCond1 = new Array<boolean>(n).fill(false);
  const buyCond = new Array<boolean>(n).fill(false);
  const stopVolume = new Array<boolean>(n).fill(false);
  const revUpThrust = new Array<boolean>(n).fill(false);
  const effortUp = new Array<boolean>(n).fill(false);
  const effortUpfail = new Array<boolean>(n).fill(false);
  const effortDown = new Array<boolean>(n).fill(false);
  const effortDownFail = new Array<boolean>(n).fill(false);
  const bcA = new Array<boolean>(n).fill(false);
  const scA = new Array<boolean>(n).fill(false);
  const tbc2 = new Array<boolean>(n).fill(false);
  const tbc3 = new Array<boolean>(n).fill(false);
  const tbc4 = new Array<boolean>(n).fill(false);
  const tbc5 = new Array<boolean>(n).fill(false);
  const fom1 = new Array<boolean>(n).fill(false);

  for (let i = 0; i < n; i++) {
    const sp = spread[i], va = volAvg[i], as = avgSpread[i];
    if (!Number.isFinite(va) || !Number.isFinite(as)) continue;

    const closeFactor = C[i] - L[i];
    const clsPosition = sp / closeFactor;
    const closePosition = closeFactor === 0 ? as : clsPosition;
    const ClosePos = C[i] <= L[i] + sp * 0.2 ? 1 : C[i] <= L[i] + sp * 0.4 ? 2 : C[i] <= L[i] + sp * 0.6 ? 3 : C[i] <= L[i] + sp * 0.8 ? 4 : 5;
    const volpos = V[i] > va * 2 ? 1 : V[i] > va * 1.3 ? 2 : V[i] > va ? 3 : (V[i] < va && V[i] < va * 0.7) ? 4 : 5;

    const upClose = C[i] >= sp * 0.7 + L[i];
    const downClose = C[i] <= sp * 0.3 + L[i];
    const aboveClose = C[i] > sp * 0.5 + L[i];
    const belowClose = C[i] < sp * 0.5 + L[i];
    const midClose = C[i] > sp * 0.3 + L[i] && C[i] < sp * 0.7 + L[i];
    const veryHighClose = closePosition < 1.35;

    const upBar = i >= 1 && C[i] > C[i - 1];
    const downBar = i >= 1 && C[i] < C[i - 1];
    const highVolume = i >= 2 && V[i] > V[i - 1] && V[i - 1] > V[i - 2];
    const lowVolume = i >= 2 && V[i] < V[i - 1] && V[i] < V[i - 2] && V[i] < va;
    const wideRangeBar = sp > 1.5 * as;
    const narrowRangeBar = sp < 0.7 * as;
    const AvgSpreadBar = sp > as;
    const freshGndHi = Number.isFinite(highestAt(H, i, 5)) && H[i] === highestAt(H, i, 5);
    const hiH1 = i >= 1 && H[i] > H[i - 1];

    // Up Thrust family.
    upThrustBar[i] = wideRangeBar && downClose && hiH1 && upmid[i] === 1;
    nut[i] = wideRangeBar && downClose && freshGndHi && highVolume;
    upThrustBartrue[i] = wideRangeBar && ClosePos === 1 && upmajor[i] > 0 && hiH1 && volpos < 4;
    upThrustCond1[i] = i >= 1 && upThrustBar[i - 1] && downBar && !narrowRangeBar;
    upThrustCond2[i] = i >= 1 && upThrustBar[i - 1] && downBar && V[i] > va * 1.3;
    upThrustCond3[i] = upThrustBar[i] && V[i] > va * 2;

    topRevBar[i] = i >= 1 && V[i - 1] > va && (i >= 2 && C[i - 1] > C[i - 2]) && spread[i - 1] > 1.5 * avgSpread[i - 1]
      && downBar && downClose && wideRangeBar && upmajor[i] > 0 && Number.isFinite(highestAt(H, i, 10)) && H[i] === highestAt(H, i, 10);
    pseudoUp[i] = (i >= 2 && C[i - 1] > C[i - 2]) && hiH1 && i >= 1 && V[i - 1] > 1.5 * va && downBar && downClose && !upThrustBar[i];
    pseudoUtCond[i] = i >= 1 && pseudoUp[i - 1] && downBar && downClose && !upThrustBar[i];
    trendChange[i] = (i >= 2 && C[i - 1] > C[i - 2]) && Number.isFinite(highestAt(H, i, 5)) && H[i] === highestAt(H, i, 5)
      && downBar && (downClose || midClose) && V[i] > va && upmajor[i] > 0 && upmid[i] > 0 && !wideRangeBar && !pseudoUp[i];

    noDemandUt[i] = upBar && narrowRangeBar && lowVolume && (aboveClose || upClose) && (upminor[i] >= 0 && upmid[i] >= 0);
    noDemandDt[i] = upBar && narrowRangeBar && lowVolume && (aboveClose || upClose) && (upminor[i] <= 0 || upmid[i] <= 0);
    noSupply[i] = downBar && narrowRangeBar && lowVolume && midClose;

    const lowestL5 = lowestAt(L, i, 5);
    lowVolTest[i] = Number.isFinite(lowestL5) && L[i] === lowestL5 && upClose && lowVolume;
    lowVolTest2[i] = i >= 1 && lowVolTest[i - 1] && upBar && upClose;

    sellCond1[i] = upThrustCond1[i] || upThrustCond2[i] || upThrustCond3[i];
    sellCond[i] = sellCond1[i] && !(i >= 1 && sellCond1[i - 1]);

    strDown0[i] = upmajor[i] < 0 && volpos < 4 && (i >= 1 && C[i - 1] < (i >= 2 ? C[i - 2] : C[i - 1])) && upBar && ClosePos > 3 && upminor[i] < 0 && upmid[i] <= 0;
    strDown[i] = volpos < 4 && (i >= 1 && C[i - 1] < (i >= 2 ? C[i - 2] : C[i - 1])) && upBar && ClosePos > 3 && upmid[i] <= 0 && upminor[i] < 0;
    strDown1[i] = upmajor[i] < 0 && V[i] > va * 1.5 && (i >= 1 && C[i - 1] < (i >= 2 ? C[i - 2] : C[i - 1])) && upBar && ClosePos > 3 && upmid[i] <= 0 && upminor[i] < 0;
    strDown2[i] = upmid[i] <= 0 && (i >= 1 && V[i - 1] < va) && upBar && veryHighClose && volpos < 4;
    strcond[i] = strDown2[i] && !strDown0[i] && !strDown[i] && !strDown1[i];

    buyCond1[i] = strDown[i] || strDown1[i];
    buyCond[i] = upBar && (i >= 1 && buyCond1[i - 1]);

    stopVolume[i] = Number.isFinite(lowestL5) && L[i] === lowestL5 && (upClose || midClose) && V[i] > 1.5 * va && upmajor[i] < 0;
    revUpThrust[i] = upBar && upClose && (i >= 1 && V[i] > V[i - 1]) && V[i] > va && wideRangeBar
      && (i >= 1 && C[i - 1] < (i >= 2 ? C[i - 2] : C[i - 1])) && (i >= 1 && C[i - 1] <= spread[i - 1] * 0.3 + L[i - 1]) && upminor[i] < 0;

    effortUp[i] = i >= 1 && H[i] > H[i - 1] && L[i] > L[i - 1] && C[i] > C[i - 1] && C[i] >= (H[i] - L[i]) * 0.7 + L[i] && sp > as && volpos < 4;
    effortUpfail[i] = i >= 1 && effortUp[i - 1] && (upThrustBar[i] || upThrustCond1[i] || upThrustCond2[i] || upThrustCond3[i] || (downBar && AvgSpreadBar));
    effortDown[i] = i >= 1 && H[i] < H[i - 1] && L[i] < L[i - 1] && C[i] < C[i - 1] && C[i] <= (H[i] - L[i]) * 0.25 + L[i] && wideRangeBar && V[i] > V[i - 1];
    effortDownFail[i] = i >= 1 && effortDown[i - 1] && ((upBar && AvgSpreadBar) || revUpThrust[i] || buyCond1[i]);

    const hiVol60 = highestAt(V, i, 60);
    bcA[i] = wideRangeBar && aboveClose && Number.isFinite(hiVol60) && V[i] === hiVol60 && upmajor[i] === 1;
    scA[i] = wideRangeBar && belowClose && Number.isFinite(hiVol60) && V[i] === hiVol60 && upmajor[i] === -1;

    // Two-bar reversal.
    const tbcd = i >= 5 && C[i - 1] < C[i - 5] && C[i - 1] < C[i - 4] && C[i - 1] < C[i - 3] && C[i - 1] < C[i - 2];
    const tbcu = i >= 5 && C[i - 1] > C[i - 5] && C[i - 1] > C[i - 4] && C[i - 1] > C[i - 3] && C[i - 1] > C[i - 2];
    const tbc1 = i >= 1 && L[i] < L[i - 1] && H[i] > H[i - 1];
    tbc2[i] = tbcd && tbc1 && V[i] > 1.2 * va && upClose;
    tbc3[i] = tbcd && tbc1 && upClose && V[i] <= 1.2 * va;
    tbc4[i] = tbcu && tbc1 && V[i] > 1.2 * va && downClose;
    tbc5[i] = tbcu && tbc1 && downClose && V[i] <= 1.2 * va;

    const Lpm = Number.isFinite(pma[i]) && pm[i] < 0.5 * pma[i];
    fom1[i] = V[i] > 1.5 * va && Lpm;
  }

  // Combined guard helpers (per Pine).
  const UT = (i: number) => upThrustBar[i] || upThrustBartrue[i] || upThrustCond1[i] || upThrustCond2[i];
  const ST = (i: number) => strDown0[i] || strDown[i] || strDown1[i] || strDown2[i];
  const lvt = (i: number) => lowVolTest[i] || lowVolTest2[i];

  const out: VpaMarker[] = [];
  const push = (i: number, position: 'aboveBar' | 'belowBar', shape: VpaMarker['shape'], text: string, color: string) =>
    out.push({ time: T[i], position, shape, text, color });

  for (let i = 0; i < n; i++) {
    // UT — Up Thrust (UT1 / UT2).
    if (toggles.ut) {
      if ((upThrustBar[i] || upThrustBartrue[i]) && !effortUpfail[i] && !sellCond[i] && !bcA[i]) push(i, 'aboveBar', 'arrowDown', 'UT1', COL_BEAR);
      if ((upThrustCond1[i] || upThrustCond2[i] || nut[i]) && !effortUpfail[i] && !sellCond[i] && !bcA[i]) push(i, 'aboveBar', 'arrowDown', 'UT2', COL_BEAR);
    }
    // TRB — Top Reversal.
    if (toggles.trb && topRevBar[i] && !sellCond[i] && !UT(i) && !effortUpfail[i]) push(i, 'aboveBar', 'arrowDown', 'TRB', COL_BEAR);
    // TC — Trend Change.
    if (toggles.tc && trendChange[i] && !effortUpfail[i]) push(i, 'aboveBar', 'arrowDown', 'TC', COL_BEAR);
    // PUT / PUC — Pseudo Up Thrust.
    if (toggles.put) {
      if (pseudoUp[i] && !effortUpfail[i]) push(i, 'aboveBar', 'arrowDown', 'PUT', COL_BEAR);
      if (pseudoUtCond[i] && !effortUpfail[i]) push(i, 'aboveBar', 'arrowDown', 'PUC', COL_BEAR);
    }
    // ND — No Demand.
    if (toggles.nd) {
      if (noDemandUt[i]) push(i, 'aboveBar', 'circle', 'ND', COL_WARN);
      else if (noDemandDt[i]) push(i, 'aboveBar', 'circle', 'ND', COL_WARN);
    }
    // NS — No Supply.
    if (toggles.ns && noSupply[i]) push(i, 'belowBar', 'circle', 'NS', COL_BULL);
    // LVT / ST — Low-Volume Supply Test.
    if (toggles.lvtst) {
      if (lowVolTest[i] && !effortDownFail[i]) push(i, 'belowBar', 'circle', 'LVT', COL_BULL);
      if (lowVolTest2[i] && !effortUp[i]) push(i, 'belowBar', 'arrowUp', 'ST', COL_BULL);
    }
    // ST — Strength signals (ST1..ST4).
    if (toggles.st) {
      const guard = !effortDownFail[i] && !effortUp[i] && !stopVolume[i];
      if (strDown0[i] && guard) push(i, 'belowBar', 'arrowUp', 'ST1', COL_BULL);
      if (strDown[i] && !strDown2[i] && guard) push(i, 'belowBar', 'arrowUp', 'ST1', COL_BULL);
      if (strDown1[i] && guard) push(i, 'belowBar', 'arrowUp', 'ST1', COL_BULL);
      if (strDown2[i] && strcond[i] && guard) push(i, 'belowBar', 'arrowUp', 'ST2', COL_BULL);
    }
    // SV — Stop Volume.
    if (toggles.sv && stopVolume[i]) push(i, 'belowBar', 'circle', ST(i) ? 'ST·SV' : 'SV', COL_BULL);
    // EU — Effort Up.
    if (toggles.eu) {
      if (effortUp[i] && !ST(i) && !buyCond[i] && !effortDownFail[i]) push(i, 'belowBar', 'arrowUp', 'EU', COL_BULL);
      else if (effortUp[i] && ST(i) && !buyCond[i]) push(i, 'belowBar', 'arrowUp', 'ST·EU', COL_BULL);
    }
    // EUF — Effort Up Fail.
    if (toggles.euf && effortUpfail[i] && !UT(i)) push(i, 'aboveBar', 'arrowDown', 'EUF', COL_BEAR);
    // ED — Effort Down.
    if (toggles.ed && effortDown[i] && !effortUpfail[i]) push(i, 'aboveBar', 'arrowDown', 'ED', COL_BEAR);
    // EDF — Effort Down Fail.
    if (toggles.edf) {
      if (effortDownFail[i] && !ST(i)) push(i, 'belowBar', 'arrowUp', 'EDF', COL_BULL);
      else if (effortDownFail[i] && ST(i)) push(i, 'belowBar', 'arrowUp', 'ST·EDF', COL_BULL);
    }
    // RUT — Reverse Up Thrust.
    if (toggles.rut && revUpThrust[i] && !ST(i)) push(i, 'belowBar', 'arrowUp', 'RUT', COL_BULL);
    // BCE — Buy Condition Exist.
    if (toggles.bce) {
      if (buyCond[i] && !ST(i) && !effortUp[i] && !lvt(i)) push(i, 'belowBar', 'arrowUp', 'BCE', COL_BULL);
      else if (buyCond[i] && !ST(i) && effortUp[i]) push(i, 'belowBar', 'arrowUp', 'EU·BCE', COL_BULL);
    }
    // SCE — Sell Condition Exist.
    if (toggles.sce && sellCond[i]) push(i, 'aboveBar', 'arrowDown', UT(i) ? 'UT·SCE' : 'SCE', COL_BEAR);
    // BC — Buying Climax.
    if (toggles.bc && bcA[i]) push(i, 'aboveBar', 'arrowDown', UT(i) ? 'UT·BC' : 'BC', COL_BEAR);
    // SC — Selling Climax.
    if (toggles.sc && scA[i]) push(i, 'belowBar', 'arrowUp', ST(i) ? 'ST·SC' : 'SC', COL_BULL);
    // TB — Two-Bar Reversal.
    if (toggles.tb) {
      if (tbc2[i]) push(i, 'belowBar', 'arrowUp', 'TB', COL_BULL);
      if (tbc3[i]) push(i, 'belowBar', 'arrowUp', 'TB', COL_WARN);
      if (tbc4[i]) push(i, 'aboveBar', 'arrowDown', 'TB', COL_BEAR);
      if (tbc5[i]) push(i, 'aboveBar', 'arrowDown', 'TB', COL_WARN);
    }
    // FOM — Ease of Movement.
    if (toggles.fom && fom1[i]) push(i, 'belowBar', 'square', 'FOM', COL_WARN);
  }
  return out;
}
